import { BusinessRegistrationService } from '../../../../_services/BusinessRegistration.Service';
import {
  BR_STATUS,
  getItemStatusCode,
  isBrPendingCode,
  isBrApprovedCode,
  sortBrListItemsByLastUpdated,
  normalizeBrApiItem,
  unwrapBrApiRows,
} from '../../../businessDocuments/utils/brDocument';
import {
  composeBrListWithNameChanges,
  getApprovedBrForNameChangeOptions,
  isBrNameChangeRequestRow,
  buildNameChangeRequestApprovalIdSet,
  withoutDuplicateNameChangeRequestRows,
} from '../../../businessDocuments/utils/brNameChange';
import {
  BR_REQUEST_KIND,
  getBrRequestKind,
  isBrNameChangeRequestKind,
} from '../../../../constants/brRequestKind';

/** 审批中(P) 无操作栏；人工审批中(PA) / 已驳回 才走草稿底栏 */
export function isBrAdminDraftBarMode(item) {
  const code = getItemStatusCode(item);
  if (code === BR_STATUS.PENDING) {
    return false;
  }
  return item?.listKind === 'pending' || code === BR_STATUS.REJECTED;
}

/** 将后端英文固定句映射为三语资源 */
export function mapAdminProcessErrorMessage(message, t) {
  const raw = String(message || '').trim();
  if (!raw) {
    return raw;
  }
  const lower = raw.toLowerCase();
  if (
    lower === 'br is approved already.'
    || lower.indexOf('br is approved already') === 0
    || lower.indexOf('business registration document status is already') === 0
  ) {
    return typeof t === 'function' ? t('lbl_EMPBE_BR_Admin_AlreadyApproved') : raw;
  }
  if (
    lower.indexOf('cannot invalidate the only in-effect') >= 0
    || lower.indexOf('cannot reject the only in-effect') >= 0
    || lower.indexOf('only valid business registration') >= 0
  ) {
    return typeof t === 'function' ? t('lbl_EMPBE_BR_Admin_RejectLastBlocked') : raw;
  }
  return raw;
}

export function normalizeAdminItem(row, listKind, requestKindFallback) {
  const requestKind = row.requestKind || row.RequestKind || requestKindFallback || BR_REQUEST_KIND.BR_DOCUMENT;
  const base = normalizeBrApiItem(row, { listKind, requestKind });
  return {
    ...base,
    listKind,
    requestKind,
    sourceApprovalId: row.sourceApprovalId ?? row.SourceApprovalId ?? null,
    sourceBusinessRegistrationId:
      row.sourceBusinessRegistrationId ?? row.SourceBusinessRegistrationId ?? null,
    resultApprovalId: row.resultApprovalId ?? row.ResultApprovalId ?? null,
    resultBusinessRegistrationId:
      row.resultBusinessRegistrationId ?? row.ResultBusinessRegistrationId ?? null,
    previousDisplayNameEn: row.previousDisplayNameEn || row.PreviousDisplayNameEn || '',
    previousDisplayNameZh: row.previousDisplayNameZh || row.PreviousDisplayNameZh || '',
    isNameChangePending: !!(row.isNameChangePending || row.IsNameChangePending),
    isNameChangeRequestCard: !!(row.isNameChangeRequestCard || row.IsNameChangeRequestCard),
    isNameChangeResultCard: !!(row.isNameChangeResultCard || row.IsNameChangeResultCard),
    isSupersededByNameChange: !!(row.isSupersededByNameChange || row.IsSupersededByNameChange),
  };
}

/**
 * GetRejectTemplatesByApprovalId → FRF templates [{ id, title, content }]
 */
export function mapApprovalRejectTemplatesToFrf(raw) {
  return (Array.isArray(raw) ? raw : [])
    .map((item, idx) => {
      if (!item || typeof item !== 'object') return null;
      const title = String(item.Title ?? item.title ?? '').trim();
      const content = String(item.Content ?? item.content ?? '').trim();
      if (!title || !content) return null;
      const orderId = Number(item.OrderId ?? item.orderId);
      const hasOrder = Number.isFinite(orderId);
      return {
        id: hasOrder ? orderId : `tpl_${idx}`,
        title,
        content,
        _order: hasOrder ? orderId : idx,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a._order - b._order)
    .map(({ id, title, content }) => ({ id, title, content }));
}

/**
 * GetRejectReasonByApprovalId 行 → FRF templates（与预填同源：RejectedReason）
 */
export function mapRejectReasonByApprovalRowsToFrf(raw) {
  return (Array.isArray(raw) ? raw : [])
    .map((row, idx) => {
      if (!row || typeof row !== 'object') return null;
      const label = String(row.RejectedReason ?? row.rejectedReason ?? '').trim();
      if (!label) return null;
      const code = String(row.AiRejectedReason ?? row.aiRejectedReason ?? '').trim();
      return {
        id: code || `rej_${idx}`,
        label,
      };
    })
    .filter(Boolean);
}

/**
 * 预填文案：以 proc_ApprovalRequest_GetRejectReasonByApprovalId 为准
 * （RejectedReason 已由 udf_GetAliyunRejectLabel 转好）。
 * 仅当 SP 无行/无文案时，才回退列表 Detail 原文（人工自由文本）。
 */
export function resolveBrPrefillRejectReason(rows, detailText) {
  const list = Array.isArray(rows) ? rows : [];
  const labels = [];
  list.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const label = String(row.RejectedReason ?? row.rejectedReason ?? '').trim();
    if (label) labels.push(label);
  });
  const spText = labels.join('; ');
  if (spText) return spText;
  return String(detailText || '').trim();
}

export function toAdminDisplayItem(item, listKind) {
  let requestKind = item.requestKind || BR_REQUEST_KIND.BR_DOCUMENT;
  const resultKind = getBrRequestKind(item);
  if (
    isBrNameChangeRequestRow(item)
    || item.isNameChangeResultCard
    || resultKind === BR_REQUEST_KIND.NAME_CHANGE_RESULT
  ) {
    requestKind = item.isNameChangeResultCard || resultKind === BR_REQUEST_KIND.NAME_CHANGE_RESULT
      ? BR_REQUEST_KIND.NAME_CHANGE_RESULT
      : BR_REQUEST_KIND.NAME_CHANGE;
  }
  return normalizeAdminItem({ ...item, requestKind }, listKind, requestKind);
}

function emptyAdminLoadResult(extra = {}) {
  return {
    pendingItems: [],
    processedItems: [],
    documentList: [],
    nameChangeSourceList: [],
    nameChanges: [],
    currentNameEn: '',
    currentNameZh: '',
    error: null,
    listLoadWarning: null,
    ...extra,
  };
}

function findNameChangeSourceBr(item, brItems) {
  return (brItems || []).find(
    (br) => String(br.approvalId) === String(item.sourceApprovalId)
      || String(br.businessRegistrationId) === String(
        item.sourceBusinessRegistrationId || item.businessRegistrationId,
      ),
  );
}

function resolveNameChangeDisplayStatus(item, listKind) {
  const raw = getItemStatusCode(item);
  if (listKind === 'pending') {
    return isBrPendingCode(raw) ? raw : BR_STATUS.PENDING_MANUAL;
  }
  if (raw === BR_STATUS.INVALID) {
    return BR_STATUS.INVALID;
  }
  return BR_STATUS.REJECTED;
}

function buildAdminNameChangeDisplayItem(item, brItems, listKind) {
  const source = findNameChangeSourceBr(item, brItems);
  const isPending = listKind === 'pending';
  const statusCode = resolveNameChangeDisplayStatus(item, listKind);
  const sourceBrId = item.sourceBusinessRegistrationId
    ?? item.SourceBusinessRegistrationId
    ?? item.businessRegistrationId
    ?? item.BusinessRegistrationId
    ?? source?.businessRegistrationId
    ?? source?.BusinessRegistrationId
    ?? null;
  return toAdminDisplayItem({
    ...(source || {}),
    ...item,
    statusCode,
    requestKind: BR_REQUEST_KIND.NAME_CHANGE,
    isNameChangePending: isPending,
    isNameChangeRequestCard: true,
    sourceBusinessRegistrationId: sourceBrId,
    displayNameEn: item.displayNameEn,
    displayNameZh: item.displayNameZh,
    mediaFileName: source?.mediaFileName || item.mediaFileName,
    ...(statusCode === BR_STATUS.REJECTED
      ? { rejectReason: item.rejectReason }
      : {}),
  }, listKind);
}

/**
 * 管理端加载 BR 的唯一入口（页面勿再直接调 GetAdminPending / GetList）。
 * - GetAdminPending：待审 / 已决 / 更名（主源）
 * - GetList：仅补齐 AdminPending 未覆盖的历史照（副源；失败时 toast，不整页清空）
 * 真·单源需后端把历史并入 GetAdminPending 后再去掉副源。
 */
export async function loadAdminBrDocuments(empCompanyId) {
  if (!empCompanyId) {
    return emptyAdminLoadResult();
  }

  const langId = Number(localStorage.getItem('currentLangId')) || 1252;
  const pendingRes = await BusinessRegistrationService.GetAdminPending(empCompanyId);
  if (!pendingRes?.isSuccessful) {
    return emptyAdminLoadResult({
      error: pendingRes?.msg || 'LOAD_FAILED',
    });
  }

  let listRows = [];
  let listLoadWarning = null;
  try {
    const listRes = await BusinessRegistrationService.GetList({ empCompanyId, langId });
    if (!listRes?.isSuccessful) {
      listLoadWarning = 'LIST_LOAD_FAILED';
    } else {
      listRows = unwrapBrApiRows(listRes?.data);
    }
  } catch (e) {
    listLoadWarning = 'LIST_LOAD_FAILED';
  }

  const rows = unwrapBrApiRows(pendingRes?.data);

  const brFromAdmin = rows
    .filter((row) => !isBrNameChangeRequestKind(row))
    .map((row) => normalizeAdminItem(row, 'processed', BR_REQUEST_KIND.BR_DOCUMENT))
    .filter((item) => item.approvalId);

  const nameChanges = rows
    .filter((row) => isBrNameChangeRequestKind(row))
    .map((row) => normalizeAdminItem(row, 'processed', BR_REQUEST_KIND.NAME_CHANGE))
    .filter((item) => item.approvalId);

  const adminBrIds = new Set(
    brFromAdmin.map((x) => Number(x.approvalId)).filter((n) => Number.isFinite(n)),
  );
  const brFromList = listRows
    .map((row) => normalizeAdminItem(row, 'processed', BR_REQUEST_KIND.BR_DOCUMENT))
    .filter((item) => {
      const id = Number(item.approvalId);
      if (!Number.isFinite(id) || id <= 0 || adminBrIds.has(id)) {
        return false;
      }
      const code = getItemStatusCode(item);
      return isBrApprovedCode(item.statusCode)
        || code === BR_STATUS.REJECTED
        || code === BR_STATUS.INVALID
        || isBrPendingCode(item.statusCode);
    });

  const brItems = composeBrListWithNameChanges(
    [...brFromAdmin, ...brFromList],
    nameChanges,
  );

  const documentList = brItems.filter(
    (item) => !item.isNameChangeRequestCard && !isBrNameChangeRequestKind(item),
  );
  const nameChangeSourceList = getApprovedBrForNameChangeOptions(documentList);

  const pendingNameChangeItems = nameChanges
    .filter((item) => isBrPendingCode(item.statusCode))
    .map((item) => buildAdminNameChangeDisplayItem(item, brItems, 'pending'));

  const rejectedNameChangeItems = nameChanges
    .filter((item) => getItemStatusCode(item) === BR_STATUS.REJECTED)
    .map((item) => buildAdminNameChangeDisplayItem(item, brItems, 'processed'));

  const invalidatedNameChangeItems = nameChanges
    .filter((item) => getItemStatusCode(item) === BR_STATUS.INVALID)
    .map((item) => buildAdminNameChangeDisplayItem(item, brItems, 'processed'));

  const nameChangeRequestItems = [
    ...pendingNameChangeItems,
    ...rejectedNameChangeItems,
    ...invalidatedNameChangeItems,
  ];
  const nameChangeRequestApprovalIds = buildNameChangeRequestApprovalIdSet(nameChangeRequestItems);

  const pendingItems = sortBrListItemsByLastUpdated([
    ...withoutDuplicateNameChangeRequestRows(
      brItems
        .filter((item) => isBrPendingCode(item.statusCode))
        .map((item) => toAdminDisplayItem(item, 'pending')),
      nameChangeRequestApprovalIds,
    ),
    ...pendingNameChangeItems,
  ]);

  const processedItems = sortBrListItemsByLastUpdated([
    ...withoutDuplicateNameChangeRequestRows(
      brItems
        .filter((item) => {
          const code = getItemStatusCode(item);
          return code === BR_STATUS.APPROVED
            || code === BR_STATUS.REJECTED
            || code === BR_STATUS.INVALID;
        })
        .map((item) => toAdminDisplayItem(item, 'processed')),
      nameChangeRequestApprovalIds,
    ),
    ...rejectedNameChangeItems,
    ...invalidatedNameChangeItems,
  ]);

  const withCurrent = [...rows]
    .map((row) => ({
      currentNameEn: row.currentNameEn || row.CurrentNameEn || '',
      currentNameZh: row.currentNameZh || row.CurrentNameZh || '',
    }))
    .find((x) => x.currentNameEn);

  return {
    pendingItems,
    processedItems,
    documentList,
    nameChangeSourceList,
    /** 供更名提交与新执照同口径做同号待审重复检查 */
    nameChanges,
    currentNameEn: withCurrent?.currentNameEn || '',
    currentNameZh: withCurrent?.currentNameZh || '',
    error: null,
    listLoadWarning,
  };
}
